/**
 * DocumentDatabaseDetailsModal Component
 *
 * Modal for viewing document database details.
 */

import React from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Alert } from '../../../components/ui/Alert';
import { Spinner } from '../../../components/ui/Spinner';
import { CopyableCode } from '../../../components/ui/CopyableCode';
import { useDocumentDatabaseDetails } from '../hooks/useDocumentDatabaseDetails';
import { formatDate } from '../../../lib/utils/format';
import { getErrorMessage } from '../../../lib/utils/errors';

interface DocumentDatabaseDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  documentDBId: string;
}

/**
 * DocumentDatabaseDetailsModal component
 */
export const DocumentDatabaseDetailsModal: React.FC<DocumentDatabaseDetailsModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  documentDBId,
}) => {
  const { data: documentDB, isLoading, error } = useDocumentDatabaseDetails(tenantId, documentDBId);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <Modal.Header>Document Database Details</Modal.Header>
      <Modal.Body>
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        )}

        {/* Error State */}
        {error && <Alert variant="error">{getErrorMessage(error)}</Alert>}

        {/* DocumentDB Details */}
        {documentDB && (
          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Basic Information
              </h3>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Database Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{documentDB.documentDBName}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Database Code</dt>
                  <dd className="mt-1">
                    <CopyableCode value={documentDB.documentDBCode} />
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Database ID</dt>
                  <dd className="mt-1">
                    <CopyableCode value={documentDB.documentDBId} />
                  </dd>
                </div>
                {documentDB.schemaVersion && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Schema Version</dt>
                    <dd className="mt-1 text-sm text-gray-900">v{documentDB.schemaVersion}</dd>
                  </div>
                )}
                {documentDB.description && (
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-gray-500">Description</dt>
                    <dd className="mt-1 text-sm text-gray-900">{documentDB.description}</dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Usage Metrics */}
            {(documentDB.documentCount !== undefined || documentDB.totalSizeBytes !== undefined) && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Usage Metrics</h3>
                <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {documentDB.documentCount !== undefined && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Document Count</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {documentDB.documentCount.toLocaleString()}
                      </dd>
                    </div>
                  )}
                  {documentDB.totalSizeBytes !== undefined && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Total Size</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {(documentDB.totalSizeBytes / 1024 / 1024).toFixed(2)} MB
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            )}

            {/* Metadata */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Metadata</h3>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Created At</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(documentDB.createdAt, 'MMM dd, yyyy HH:mm')}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Updated At</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(documentDB.updatedAt, 'MMM dd, yyyy HH:mm')}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
